import type { SearchSection } from '@setplex/tria-api'
import type { MotnItem } from '~/entities/motn'
import type {
  SearchResultClickPayload,
  SearchResultContentType,
} from '~/features/analytix'

import { MediaTypes } from '@setplex/tria-api'
import {
  SEARCH_CARD_KIND_CATCHUP,
  SEARCH_CARD_KIND_WATCH_NOW,
} from '~/entities/hybrid-search'

/**
 * ET-1 coordinates every result card carries so that a click handler can report
 * the selection without re-deriving it from the grid.
 */
export type SearchResultAnalyticsArgs = {
  section: SearchSection
  position: number
  results: number
  contentType?: SearchResultContentType
  /**
   * Title of the result as ET-1.4 wants it per content type: the channel name
   * for a channel or a catch-up, the movie, TV show or event name otherwise.
   * Reported separately from the `name` a card hands its own handler, which is
   * a display title — a channel passes its number along with its name there.
   */
  contentName?: string
  programTitle?: string
}

/**
 * External results never reach the section grid — they come from their own MOTN
 * model — so their section is fixed rather than read off the item.
 */
export const EXTERNAL_SECTION: SearchSection = 'EXTERNAL'

/** ET-1 coordinates attached to every external result. */
export type MotnResultAnalyticsFields = {
  analyticsPosition: number
  analyticsResults: number
  analyticsContentType?: SearchResultContentType
}

/** The same coordinates for a card inside a section grid. */
export type SearchResultAnalyticsFields = MotnResultAnalyticsFields & {
  analyticsSection: SearchSection
}

/**
 * Card kind → ET-1.2 content type. A Watch Now card is a channel tuned to the
 * programme it advertises, so it reports as `tv`; a Recently Aired one opens
 * catch-up playback and reports as `catchup`.
 */
const CONTENT_TYPE_BY_CARD_KIND: Partial<
  Record<string, SearchResultContentType>
> = {
  [MediaTypes.VOD]: 'vod',
  [MediaTypes.TV_SHOW]: 'tvshow',
  [MediaTypes.TV_CHANNEL]: 'tv',
  [MediaTypes.LIVE_EVENT]: 'event',
  [SEARCH_CARD_KIND_WATCH_NOW]: 'tv',
  [SEARCH_CARD_KIND_CATCHUP]: 'catchup',
}

export function toSearchResultContentType(
  cardKind: string | undefined
): SearchResultContentType | undefined {
  return cardKind == null ? undefined : CONTENT_TYPE_BY_CARD_KIND[cardKind]
}

/**
 * ET-1 payload for an in-platform result, which is identified by its id
 * (ET-1.1) — external results go through {@link toMotnContentType} instead.
 */
export function toResultClickPayload({
  section,
  position,
  results,
  contentType,
  contentName,
  programTitle,
  id,
}: SearchResultAnalyticsArgs & {
  id?: number | string
}): SearchResultClickPayload {
  return {
    section,
    position,
    results,
    contentType,
    contentName,
    programTitle,
    contentId: id,
  }
}

/**
 * Strips the ET-1 coordinates back off a click payload so what remains is
 * exactly the arguments the card's original handler expects.
 */
export function withoutAnalyticsArgs<T extends SearchResultAnalyticsArgs>(
  args: T
): Omit<T, keyof SearchResultAnalyticsArgs> {
  const {
    section: _section,
    position: _position,
    results: _results,
    contentType: _contentType,
    contentName: _contentName,
    programTitle: _programTitle,
    ...rest
  } = args
  return rest
}

/** External results are movies or series, mirroring the MOTN details event. */
export function toMotnContentType(
  item: MotnItem
): SearchResultContentType | undefined {
  if (item.contentType == null) return undefined
  return item.contentType === 'MOTN_TV_SHOW' ? 'tvshow' : 'vod'
}

/**
 * Attaches the ET-1 coordinates to a section's cards. The section comes from the
 * group the card was rendered in rather than from the card itself, so a live
 * event that has finished reports `CATCHUPS` while an airing one reports
 * `LIVE_EVENTS` even though both render the same card.
 */
export function withResultAnalytics<T extends { cardKind?: string }>(
  cards: T[],
  section: SearchSection
): Array<T & SearchResultAnalyticsFields> {
  return cards.map((card, position) => ({
    ...card,
    analyticsSection: section,
    analyticsPosition: position,
    analyticsResults: cards.length,
    analyticsContentType: toSearchResultContentType(card.cardKind),
  }))
}

/** The same coordinates for external results, whose section is always fixed. */
export function withMotnResultAnalytics<T extends MotnItem>(
  items: T[]
): Array<T & MotnResultAnalyticsFields> {
  return items.map((item, position) => ({
    ...item,
    analyticsPosition: position,
    analyticsResults: items.length,
    analyticsContentType: toMotnContentType(item),
  }))
}
